import * as React from "react";
import {cx} from "emotion";

import "./step-2-edit-lists.scss";

import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {KeyLoader2}             from "@Common/react/key-loader-2";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {UseState}               from "@Common/react/use-state";
import {changeNestedValue}      from "@Common/utils/arr-path";
import {sum}                    from "@Common/utils/collections";

import {Header} from "../../common/header/header";

import {HeaderTabs} from "./header-tabs/header-tabs";
import {PerMl}      from "./per-ml/per-ml";

export const Step2EditLists = ({entityId, next}) => cs(
    consumeContext("apis"),
    ["state", (_, next) => UseState({
        initValue: null,
        next,
    })],
    ["mailingLists", ({state, apis}, next) => KeyLoader2({
        keys: state.value?.lists?.map((l) => l.idList).filter(v=>v),
        load: apis.next.mailing.getMailingListDetails,
        next,
    })],
    ["perMl", ({state}, next) => PerMl({
        listIndex: state.value?.selectedIndex??0,
        entityId,
        next,
    })],
    ({apis, state, perMl, mailingLists}) => next({
        ready: state.value,

        prepare: async ({selectedCRMLists, defaultName, defaultName2, replaceName}) => {
            state.onChange({loading: true})
            if(!replaceName) {
                defaultName = ''
            }
            const result = await apis.next.mailing.crm.getCrmListAddress({entityId, crmList: selectedCRMLists, defaultName, defaultName2});
            if (result.result === "success") {
                state.onChange({
                    lists: result.crmList,
                    selectedIndex: 0,
                    loading: false,
                });
            } else if (result.result === "failure") {
                state.onChange({
                    errors: result.errors?.msg ?? "Unknown error",
                    loading: false,
                });
            }
        },
        render: ({onBack, onClose}) => (
            <div className="step2-edit-lists-1or">
                {state.value?.loading ? (<>
                    {Header({
                        icon: require("../../../../mailing-icons/listtype-crm.png"),
                        title: "Import CRM List",
                        subTitle: "Loading...",
                    })}
                    <div className="loading" xd-qa-type="label" xd-qa-name="loading">
                        Please wait while we import your list(s). You can either wait on this screen, or return in a few minutes after they've loaded into your account.
                    </div>
                    <div className="footer">
                        <button {...{
                            className: "cancel",
                            onClick: () => {
                                state.onChange(null);
                                onBack()
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Back",
                        }}>
                            Back
                        </button>
                        <button {...{
                            className: "next",
                            onClick: () => {
                                onClose();
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Close",
                        }}>
                            Close
                        </button>
                    </div>
                </>) : state.value?.errors ? (<>
                    {Header({
                        icon: require("../../../../mailing-icons/listtype-crm.png"),
                        title: "Import CRM List",
                        subTitle: "",
                    })}
                    <div className="error">
                        {state.value.errors}
                    </div>
                    <div className="footer">
                        <button {...{
                            className: "cancel",
                            onClick: () => {
                                state.onChange(null);
                                onBack()
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Back",
                        }}>
                            Back
                        </button>
                        <button {...{
                            className: "next",
                            onClick: () => {
                                onClose();
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Close",
                        }}>
                            Close
                        </button>
                    </div>
                </>) : (<>
                    {Header({
                        icon: require("../../../../mailing-icons/listtype-crm.png"),
                        title: "Import CRM List",
                        subTitle: `Imported ${state.value.lists.filter((l) => l.idList).length} lists, with total ${mailingLists.loading ? "--" : sum(state.value.lists, ({idList}) => mailingLists.getValue(idList)?.addresses.length ?? 0)} addresses`,
                    })}

                    {HeaderTabs({
                        tabs: state.value.lists.map((list, i) => ({
                            label: list.name.toUpperCase(),
                            hasError: list.error,
                            badge: mailingLists.getValue(list.idList)?.addresses.filter((a) => !a.status || a.status === "PASSED").length,
                            selected: i === state.value.selectedIndex,
                            onSelect: () => stateChangeNestedValue(state, ["selectedIndex"], () => i),
                        })),
                    })}

                    {(() => {
                        const list = state.value.lists[state.value.selectedIndex];
                        if (list.error) {
                            return (
                                <div className="error">
                                    {list.error}
                                </div>
                            );
                        }

                        const mlId = list.idList;
                        const ml = mailingLists.getValue(mlId);
                        return perMl.render({
                            addresses: {
                                value: ml?.addresses,
                                change: (change) => mailingLists.changeValue(mlId, (ml) => changeNestedValue(ml, ["addresses"], change)),
                            },
                            loading: mailingLists.isLoading(mlId),
                            countryCode: ml?.country ?? "US",
                            mlId,
                            count: state.value.lists.length,
                            getAllMlIds: () => state.value.lists.map((r) => r.idList).filter(v=>v),
                        });
                    })()}

                    <div className="footer">
                        <button {...{
                            className: "cancel",
                            onClick: () => {
                                state.onChange(null);
                                onBack()
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Back",
                        }}>
                            Back
                        </button>
                        <button {...{
                            className: cx("next", {disabled: !perMl.valid}),
                            onClick: () => {
                                if (!perMl.valid) {
                                    perMl.highlightErrors();
                                } else {
                                    onClose(state.value.lists.map((r) => r.idList));
                                }

                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "CRM Close",
                        }}>
                            Close
                        </button>
                    </div>
                </>)}
            </div>
        ),
    }),
);
